import { type Action, type AnyAction, combineReducers } from 'redux';

import type { PlainJsStoreOrmData } from 'js/src/data/plain-js-store-orm';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import {
    deleteTranscript,
    disableTranscriptOverlays,
    endLexicalControllingItsOwnState,
    endTimingMode,
    overwriteAllLexicalEditors,
    removeLexicalEditor,
    removeTranscriptTranslation,
    setSceneConversionModalVisible,
    setTranscriptCurrentTime,
    setTranscriptLanguage,
    setTranscriptTabVisibility,
    startLexicalControllingItsOwnState,
    startTimingMode,
    storeLexicalEditor,
    storeRawTranscript,
    storeTranscriptTranslations,
    toggleOriginal,
} from 'js/src/data/transcript/transcript-actions';
import type {
    LexicalEditorsDataType,
    TranscriptData,
    TranscriptTranslations,
} from 'js/src/data/transcript/transcript-types';
import type { DeepReadonly } from 'js/src/libs/consts';

type StoredTranscript = DeepReadonly<TranscriptData>;
type StoredTranslations = DeepReadonly<TranscriptTranslations>;
type TranscriptsTable = PlainJsStoreOrmData<StoredTranscript, string>;

const NO_TRANSLATIONS: StoredTranslations = {};

const toStoredTranslations = (translations: StoredTranslations): StoredTranslations =>
    Object.keys(translations).length === 0 ? NO_TRANSLATIONS : translations;

const updateTranscript = (
    state: TranscriptsTable,
    sceneGroupId: string,
    changes: Partial<StoredTranscript>,
): TranscriptsTable => {
    const orm = new PlainJsStoreORM(state);
    if (!orm.contains(sceneGroupId)) {
        return state;
    }
    orm.updateById(sceneGroupId, (transcript) => {
        const hasChanges = (Object.keys(changes) as (keyof StoredTranscript)[]).some(
            (key) => transcript[key] !== changes[key],
        );
        return hasChanges ? { ...transcript, ...changes } : transcript;
    });
    return orm.data;
};

export function transcripts(
    state: TranscriptsTable = PlainJsStoreORM.createEmpty<StoredTranscript, string>().data,
    action: AnyAction,
): TranscriptsTable {
    if (storeRawTranscript.match(action)) {
        const { sceneGroupId, editorState, isOriginal, language, translations } = action.payload;
        const orm = new PlainJsStoreORM(state);
        if (!orm.contains(sceneGroupId)) {
            orm.pushItem(
                {
                    sceneGroupId,
                    editorState,
                    translations: translations && toStoredTranslations(translations),
                    isOriginal,
                    disableOverlays: false,
                    language,
                },
                'sceneGroupId',
            );
            return orm.data;
        }
        return updateTranscript(
            state,
            sceneGroupId,
            language ? { editorState, isOriginal, language } : { editorState, isOriginal },
        );
    }
    if (toggleOriginal.match(action)) {
        const { sceneGroupId } = action.payload;
        const transcript = new PlainJsStoreORM(state).getByIdOrThrow(sceneGroupId);
        return updateTranscript(state, sceneGroupId, { isOriginal: !transcript.isOriginal });
    }
    if (setTranscriptLanguage.match(action)) {
        return updateTranscript(state, action.payload.sceneGroupId, {
            language: action.payload.language,
        });
    }
    if (disableTranscriptOverlays.match(action)) {
        return updateTranscript(state, action.payload.sceneGroupId, { disableOverlays: true });
    }
    if (deleteTranscript.match(action)) {
        const orm = new PlainJsStoreORM(state);
        orm.delete([action.payload.sceneGroupId]);
        return orm.data;
    }
    if (storeTranscriptTranslations.match(action)) {
        return updateTranscript(state, action.payload.sceneGroupId, {
            translations: toStoredTranslations(action.payload.translations),
        });
    }
    if (removeTranscriptTranslation.match(action)) {
        return updateTranscript(state, action.payload.sceneGroupId, {
            translations: NO_TRANSLATIONS,
        });
    }
    return state;
}

export function transcript_visible(state = false, action: AnyAction) {
    if (setTranscriptTabVisibility.match(action)) {
        return action.payload;
    }
    return state;
}

export function scene_conversion_modal_visible(state = false, action: AnyAction) {
    if (setSceneConversionModalVisible.match(action)) {
        return action.payload;
    }
    return state;
}

type CurrentTimeState = Readonly<{
    sceneGroupId: string | null;
    sceneId: string | null;
    currentTimeMillis: number;
}>;

const initialCurrentTimeState: CurrentTimeState = {
    sceneGroupId: null,
    sceneId: null,
    currentTimeMillis: 0,
};

/**
 * Holds transcript current time and the scene group id & scene id that it should belongs to
 * It is set when seeking the transcript
 *
 * @param sceneGroupId - The scene group id of the active transcript
 * @param sceneId - The scene id of the active transcript
 * @param currentTimeMillis - The current time in milliseconds relative to the start of the scene group.
 *
 * This is "after" trimming meaning time 0 is always at the start of the scene group.
 * This is the time we start scene group preview from and is different from the preview render time
 *
 * We need 2 different times because editing text on a scene (or overlay) is only possible
 * by seeking to the midpoint of the overlay even if we select the last word in the overlay.
 * However, the preview should ideally start playing from the selected word (or wherever).
 *
 * By associating the current time as the time to start preview from, we can seek luminary
 * to midpoint of overlay if word is anywhere inside overlay for text editing while saving
 * the start time of the selected word as current time to start preview from.
 *
 * Note: While luminary is playing, the current time should be read directly from luminary.currentTime, rather
 * than using the state here. That's because the currentTime is not constantly sync'd to redux for performance reasons.
 */
export function current_time(
    state: CurrentTimeState = initialCurrentTimeState,
    action: AnyAction,
): CurrentTimeState {
    if (setTranscriptCurrentTime.match(action)) {
        const { sceneGroupId, sceneId, currentTimeMillis } = action.payload;
        if (
            state.sceneGroupId === sceneGroupId &&
            state.sceneId === sceneId &&
            state.currentTimeMillis === currentTimeMillis
        ) {
            return state;
        }
        return { sceneGroupId, sceneId, currentTimeMillis };
    }
    return state;
}

type LexicalEditorsTable = PlainJsStoreOrmData<LexicalEditorsDataType, string>;

const setMainLexicalControlsItsOwnState = (
    state: LexicalEditorsTable,
    sceneGroupId: string,
    lexicalControlsItsOwnState: boolean,
) => {
    const orm = new PlainJsStoreORM(state);
    if (!orm.contains(sceneGroupId)) {
        return state;
    }
    orm.updateById(sceneGroupId, (item) =>
        item.lexicalControlsItsOwnState === lexicalControlsItsOwnState
            ? item
            : { ...item, lexicalControlsItsOwnState },
    );
    return orm.data;
};

export function lexical_editors(
    state: LexicalEditorsTable = PlainJsStoreORM.createEmpty<LexicalEditorsDataType, string>().data,
    action: Action,
): LexicalEditorsTable {
    if (storeLexicalEditor.match(action)) {
        const orm = new PlainJsStoreORM(state);
        orm.pushItem(action.payload, 'sceneGroupId');
        return orm.data;
    }
    if (removeLexicalEditor.match(action)) {
        const orm = new PlainJsStoreORM(state);
        orm.delete([action.payload]);
        return orm.data;
    }
    if (overwriteAllLexicalEditors.match(action)) {
        const orm = PlainJsStoreORM.createEmpty<LexicalEditorsDataType, string>();
        action.payload.forEach((lexicalEditorObj) => {
            orm.pushItem(lexicalEditorObj, 'sceneGroupId');
        });
        return orm.data;
    }
    if (startLexicalControllingItsOwnState.match(action)) {
        if (action.payload.langCode) {
            return state;
        }
        return setMainLexicalControlsItsOwnState(state, action.payload.sceneGroupId, true);
    }
    if (endLexicalControllingItsOwnState.match(action)) {
        if (action.payload.langCode) {
            return state;
        }
        return setMainLexicalControlsItsOwnState(state, action.payload.sceneGroupId, false);
    }
    return state;
}

export type TimingModeState = Readonly<{
    assetId: string | null;
    sceneId: string | null;
    sceneGroupId: string | null;
}>;

const initialTimingModeState: TimingModeState = {
    assetId: null,
    sceneId: null,
    sceneGroupId: null,
};

export function timing_mode(
    state: TimingModeState = initialTimingModeState,
    action: AnyAction,
): TimingModeState {
    if (startTimingMode.match(action)) {
        const { assetId, sceneId, sceneGroupId } = action.payload;
        if (
            state.assetId === assetId &&
            state.sceneId === sceneId &&
            state.sceneGroupId === sceneGroupId
        ) {
            return state;
        }
        return { assetId, sceneId, sceneGroupId };
    }
    if (endTimingMode.match(action)) {
        return initialTimingModeState;
    }
    return state;
}

export const undoable_transcript = combineReducers({
    transcripts,
});

export const transcript = combineReducers({
    transcript_visible,
    scene_conversion_modal_visible,
    lexical_editors,
    current_time,
    timing_mode,
});

export type TranscriptState = ReturnType<typeof transcript>;
